import type { DatasetDescriptor } from "../rdf/types";

/**
 * The full manifest of bundled ontologies. Drives the Ontologies tab, the
 * Files tab, the dataset switcher, version selection, and Visualizer node
 * coloring-by-ontology. Every file is a static Turtle snapshot fetched from
 * its canonical source at build time. User-uploaded ontologies are appended
 * to this list at runtime (see state/user-datasets.ts) — same descriptor
 * shape, `origin: "user"`.
 *
 * Each ontology carries a `versions` array (newest first). Only one version
 * is bundled per ontology today, but the whole pipeline — registry, store
 * loading, version switcher UI — is version-aware, so adding an older
 * snapshot is just another entry here plus its Turtle file.
 */
export const DATASET_REGISTRY: DatasetDescriptor[] = [
  {
    id: "rdf",
    label: "RDF — Resource Description Framework",
    shortLabel: "RDF",
    description:
      "The base graph data model: resources, properties, and rdf:type — everything else in this app builds on it.",
    namespaceIri: "http://www.w3.org/1999/02/22-rdf-syntax-ns#",
    sourceUrl: "https://www.w3.org/1999/02/22-rdf-syntax-ns#",
    license: "W3C Document License",
    accentColor: "#f97316",
    origin: "bundled",
    enabledByDefault: true,
    versions: [
      {
        id: "1.1",
        label: "v1.1",
        releaseDate: "2014-02-25",
        note: "RDF 1.1 (W3C Recommendation)",
        files: [
          { id: "rdf-ttl", fileName: "rdf.ttl", path: "data/ontologies/rdf.ttl", format: "turtle" },
        ],
      },
    ],
  },
  {
    id: "rdfs",
    label: "RDFS — RDF Schema",
    shortLabel: "RDFS",
    description:
      "Class/property hierarchies and domain/range on top of RDF: rdfs:Class, rdfs:subClassOf, rdfs:domain, rdfs:range.",
    namespaceIri: "http://www.w3.org/2000/01/rdf-schema#",
    sourceUrl: "https://www.w3.org/2000/01/rdf-schema#",
    license: "W3C Document License",
    accentColor: "#fb923c",
    origin: "bundled",
    enabledByDefault: true,
    versions: [
      {
        id: "1.1",
        label: "v1.1",
        releaseDate: "2014-02-25",
        note: "RDF Schema 1.1 (W3C Recommendation)",
        files: [
          { id: "rdfs-ttl", fileName: "rdfs.ttl", path: "data/ontologies/rdfs.ttl", format: "turtle" },
        ],
      },
    ],
  },
  {
    id: "owl",
    label: "OWL — Web Ontology Language",
    shortLabel: "OWL",
    description:
      "Richer class expressions and axioms over RDFS: owl:Class, object/datatype properties, equivalentClass, restrictions.",
    namespaceIri: "http://www.w3.org/2002/07/owl#",
    sourceUrl: "https://www.w3.org/2002/07/owl#",
    license: "W3C Document License",
    accentColor: "#eab308",
    origin: "bundled",
    enabledByDefault: true,
    versions: [
      {
        id: "2",
        label: "v2",
        releaseDate: "2012-12-11",
        note: "OWL 2 (W3C Recommendation, Second Edition)",
        files: [
          { id: "owl-ttl", fileName: "owl.ttl", path: "data/ontologies/owl.ttl", format: "turtle" },
        ],
      },
    ],
  },
  {
    id: "shacl",
    label: "SHACL — Shapes Constraint Language",
    shortLabel: "SHACL",
    description:
      "Validates RDF graphs against shapes: sh:NodeShape, sh:PropertyShape, cardinality/datatype/class constraints.",
    namespaceIri: "http://www.w3.org/ns/shacl#",
    sourceUrl: "https://www.w3.org/ns/shacl#",
    license: "W3C Document License",
    accentColor: "#22c55e",
    origin: "bundled",
    enabledByDefault: true,
    versions: [
      {
        id: "1.0",
        label: "v1.0",
        releaseDate: "2017-07-20",
        note: "SHACL (W3C Recommendation)",
        files: [
          { id: "shacl-ttl", fileName: "shacl.ttl", path: "data/ontologies/shacl.ttl", format: "turtle" },
        ],
      },
    ],
  },
  {
    id: "prov-o",
    label: "PROV-O — Provenance Ontology",
    shortLabel: "PROV-O",
    description:
      "Models provenance: prov:Entity, prov:Activity, prov:Agent, and the relations connecting who/what/when produced data.",
    namespaceIri: "http://www.w3.org/ns/prov#",
    sourceUrl: "https://www.w3.org/ns/prov-o#",
    license: "W3C Document License",
    accentColor: "#06b6d4",
    origin: "bundled",
    enabledByDefault: true,
    versions: [
      {
        id: "2013-04-30",
        label: "REC 2013-04-30",
        releaseDate: "2013-04-30",
        note: "PROV-O (W3C Recommendation)",
        files: [
          { id: "prov-o-ttl", fileName: "prov-o.ttl", path: "data/ontologies/prov-o.ttl", format: "turtle" },
        ],
      },
    ],
  },
  {
    id: "qudt",
    label: "QUDT — Quantities, Units, Dimensions & Types",
    shortLabel: "QUDT",
    description:
      "Core schema for quantities and units (qudt:Unit, qudt:QuantityKind, dimension vectors), plus a small hand-authored sample of ~15 SI units and quantity kinds as real individuals.",
    namespaceIri: "http://qudt.org/schema/qudt/",
    sourceUrl: "https://qudt.org/2.1/schema/qudt",
    license: "Creative Commons Attribution 4.0 (CC BY 4.0)",
    accentColor: "#a855f7",
    origin: "bundled",
    enabledByDefault: true,
    versions: [
      {
        id: "2.1",
        label: "v2.1",
        note: "QUDT schema 2.1 snapshot",
        files: [
          { id: "qudt-core-ttl", fileName: "qudt-core.ttl", path: "data/ontologies/qudt-core.ttl", format: "turtle" },
          { id: "qudt-sample-ttl", fileName: "qudt-sample.ttl", path: "data/ontologies/qudt-sample.ttl", format: "turtle" },
        ],
      },
    ],
  },
  {
    id: "saref",
    label: "SAREF — Smart Applications REFerence ontology",
    shortLabel: "SAREF",
    description:
      "ETSI's core ontology for smart appliances/IoT devices: saref:Device, saref:Function, saref:Measurement, saref:Command.",
    namespaceIri: "https://saref.etsi.org/core/",
    sourceUrl: "https://saref.etsi.org/core/v3.1.1/saref.ttl",
    license: "ETSI Software License",
    accentColor: "#3b82f6",
    origin: "bundled",
    enabledByDefault: true,
    versions: [
      {
        id: "3.1.1",
        label: "v3.1.1",
        releaseDate: "2020-06-15",
        note: "ETSI TS 103 264 V3.1.1",
        files: [
          { id: "saref-core-ttl", fileName: "saref-core.ttl", path: "data/ontologies/saref-core.ttl", format: "turtle" },
        ],
      },
    ],
  },
  {
    id: "saref4ener",
    label: "SAREF4ENER — SAREF extension for energy flexibility",
    shortLabel: "SAREF4ENER",
    description:
      "Extends SAREF with demand-response/energy-flexibility concepts (s4ener:PowerLimitationProfile, load-shifting commands) — also a real-world example of cross-ontology references the Explorer resolves back into SAREF core.",
    namespaceIri: "https://saref.etsi.org/saref4ener/",
    sourceUrl: "https://saref.etsi.org/saref4ener/v1.2.1/saref4ener.ttl",
    license: "ETSI Software License",
    accentColor: "#6366f1",
    origin: "bundled",
    enabledByDefault: true,
    versions: [
      {
        id: "1.2.1",
        label: "v1.2.1",
        releaseDate: "2020-06-15",
        note: "ETSI TS 103 410-1 V1.2.1",
        files: [
          {
            id: "saref4ener-ttl",
            fileName: "saref4ener.ttl",
            path: "data/ontologies/saref4ener.ttl",
            format: "turtle",
          },
        ],
      },
    ],
  },
  {
    id: "example-shapes",
    label: "Example SHACL Shapes",
    shortLabel: "Examples",
    description:
      "Three hand-authored NodeShapes constraining classes from SAREF, PROV-O, and QUDT — a real, cross-ontology usage example, since none of the vocabulary files above ship instance-level shape data of their own.",
    namespaceIri: "https://ontologies.neo-data-models.dev/shapes/examples#",
    sourceUrl: "https://ontologies.neo-data-models.dev/shapes/examples#",
    license: "Creative Commons Attribution 4.0 (CC BY 4.0)",
    accentColor: "#84cc16",
    origin: "bundled",
    enabledByDefault: true,
    versions: [
      {
        id: "1.0",
        label: "v1.0",
        note: "Hand-authored examples for this app",
        files: [
          {
            id: "example-shapes-ttl",
            fileName: "example-shapes.ttl",
            path: "data/ontologies/example-shapes.ttl",
            format: "turtle",
          },
        ],
      },
    ],
  },
  {
    id: "bfo",
    label: "BFO — Basic Formal Ontology",
    shortLabel: "BFO",
    description:
      "The OBO Foundry's upper ontology of continuants and occurrents, representing the OBO family of biomedical/scientific ontologies in this demo.",
    namespaceIri: "http://purl.obolibrary.org/obo/",
    sourceUrl:
      "https://raw.githubusercontent.com/BFO-ontology/BFO-2020/master/21838-2/owl/bfo-core.ttl",
    license: "Creative Commons Attribution 4.0 (CC BY 4.0)",
    accentColor: "#ec4899",
    origin: "bundled",
    enabledByDefault: true,
    versions: [
      {
        id: "2020",
        label: "BFO 2020",
        releaseDate: "2020-01-01",
        note: "ISO/IEC 21838-2 (BFO 2020)",
        files: [
          { id: "bfo-core-ttl", fileName: "bfo-core.ttl", path: "data/ontologies/bfo-core.ttl", format: "turtle" },
        ],
      },
    ],
  },
];

export function getDatasetById(id: string): DatasetDescriptor | undefined {
  return DATASET_REGISTRY.find((dataset) => dataset.id === id);
}
